import React, { useEffect, useRef, useState } from 'react';
import {
  BarColorCode,
  CONST_SITE_LABEL,
  exportJSONToCSV,
  PLATFORM,
  toWholeStringByUnderscore,
  setScopedItem,
  STORAGE_KEYS,
} from '../components/common/Helper';
import DebugService from '../services/DebugService';
import SnackbarNotification, { SNACKBAR_THEME } from '../components/common/SnackbarNotification';
import GlobalScanApiService from '../services/GlobalScanApiService';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FiDownload } from 'react-icons/fi';
import { useAuth } from '../state/AuthContext';

const PANEL_STYLES = {
  background: '#ffffff',
  border: '1px solid #e6e9f0',
  borderRadius: 16,
  boxShadow: '0 1px 2px rgba(16,24,40,.04),0 4px 14px rgba(16,24,40,.05)',
  overflow: 'hidden',
};
const PANEL_HEAD = {
  background: '#4d5a73',
  color: '#fff',
  textAlign: 'center',
  fontWeight: 700,
  fontSize: 14,
  padding: '10px 14px',
};
const PANEL_BODY = {
  padding: '12px',
  background: 'linear-gradient(180deg,#e8f0fb,#dfeaf8)',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#fff', border: '1px solid #ccc', padding: '0 8px 4px', borderRadius: 8 }}>
        <p style={{ fontWeight: 700, marginBottom: 4, paddingTop: 6 }}>{label}</p>
        {payload.map((item, i) =>
          item.value !== 0 ? (
            <p key={i} style={{ color: item.color, fontSize: 13 }}>
              {item.name}: {item.value}
            </p>
          ) : null
        )}
      </div>
    );
  }
  return null;
};

export default function GlobalScan() {
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarStatus, setSnackbarStatus] = useState(null);
  const [scanResult, setScanResult] = useState([]);
  const [scanParsedResult, setScanParsedResult] = useState({});
  const [parsedResultSummary, setParsedResultSummary] = useState({});
  const [bifurcateSummary, setBifurcateSummary] = useState({});
  const [globalServices, setGlobalServices] = useState({});
  const [exportData, setExportData] = useState({});
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const [isScanAvailable, setIsScanAvailable] = useState(false);
  const [globalScanParsedResultApiData, setGlobalScanParsedResultApiData] = useState({});
  const [globalScanResultApiData, setGlobalScanResultApiData] = useState({});

  // Region summary
  const [regionList, setRegionList] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedRegionDetails, setSelectedRegionDetails] = useState({});

  // Chart
  const [chartData, setChartData] = useState([]);
  const [chartKeys, setChartKeys] = useState([]);

  // Popup (service detail on cell click)
  const [popupService, setPopupService] = useState(null);

  const scanResultSelectRef = useRef('');
  const { selectedAccount, selectedPlatform } = useAuth();

  const accountScanHandler = () => {
    setIsLoading(true);
    setSnackbarMessage(null);
    setSnackbarStatus(null);

    if (selectedPlatform === PLATFORM.AWS) {
      GlobalScanApiService.scanAccount(selectedAccount)
        .then(data => {
          if (data['data']) {
            setIsScanAvailable(false);
            setSnackbarStatus('success');
            setSnackbarMessage('Scan initiated successfully. This will take 10-15 minutes to complete.');
          }
          if (data['error']) {
            setSnackbarStatus('failed');
            setSnackbarMessage(data['error']?.message ?? data['error'] ?? 'Something went wrong');
          }
          setIsLoading(false);
        })
        .catch(() => {
          setSnackbarStatus('failed');
          setSnackbarMessage('Something went wrong. Please try later');
          setIsLoading(false);
        });
    } else {
      GlobalScanApiService.scanAzureAccount(selectedAccount)
        .then(data => {
          if (data['data']) {
            setIsScanAvailable(false);
            setSnackbarStatus('success');
            setSnackbarMessage('Scan initiated successfully. This will take 10-15 minutes to complete.');
          }
          if (data['error']) {
            setSnackbarStatus('failed');
            setSnackbarMessage(data['error']?.message ?? data['error'] ?? 'Something went wrong');
          }
          setIsLoading(false);
        })
        .catch(() => {
          setSnackbarStatus('failed');
          setSnackbarMessage('Something went wrong. Please try later');
          setIsLoading(false);
        });
    }
  };

  const processParsedData = (data) => {
    if (!data['data']) return;
    setScanParsedResult(data['data'] ?? {});

    if (data['globalServices']) {
      setGlobalServices(data['globalServices'] ?? {});
    }

    // Summary totals
    const summaryResult = { regions: Object.keys(data['data']).length };
    const regionKeyList = Object.keys(Object.values(data['data'])[0] ?? {});
    regionKeyList.forEach(k => { summaryResult[k] = 0; });

    for (const [, value] of Object.entries(data['data'])) {
      for (const [regionKey, regionValue] of Object.entries(value)) {
        summaryResult[regionKey] += (regionValue ?? 0);
      }
    }
    setParsedResultSummary(summaryResult);

    // Export data
    const summaryNoRegion = Object.assign({}, summaryResult);
    delete summaryNoRegion['regions'];
    const completeData = { ...data['data'] };
    for (const [sKey, sVal] of Object.entries(summaryNoRegion)) {
      if (sVal === 0) {
        for (const regionKey of Object.keys(completeData)) {
          delete completeData[regionKey][sKey];
        }
      }
    }
    setExportData({ ...completeData, Total: summaryResult });

    // Bifurcate for popup details
    const bifurcateData = { regions: Object.keys(data['data']) };
    const serviceNameList = Object.keys(Object.values(data['data'])[0] ?? {});
    serviceNameList.forEach(svc => {
      bifurcateData[svc] = {};
      for (const [regionKey, regionValue] of Object.entries(data['data'])) {
        bifurcateData[svc][regionKey] = regionValue[svc];
      }
    });
    setBifurcateSummary(bifurcateData);

    setIsLoading(false);
  };

  const processAzureParsedData = (data) => {
    if (!data['data']) return;
    setScanParsedResult(data['data'] ?? {});

    if (data['globalServices']) {
      setGlobalServices(data['globalServices'] ?? {});
    }

    const summaryResult = { regions: Object.keys(data['data']).length };
    for (const [, value] of Object.entries(data['data'])) {
      for (const [regionKey, regionValue] of Object.entries(value)) {
        summaryResult[regionKey] = (summaryResult[regionKey] ?? 0) + (regionValue ?? 0);
      }
    }
    setParsedResultSummary(summaryResult);

    const completeData = { ...data['data'] };
    setExportData({ ...completeData, Total: summaryResult });

    const bifurcateData = { regions: Object.keys(data['data']) };
    const serviceNameList = Object.keys(Object.values(data['data'])[0] ?? {});
    serviceNameList.forEach(svc => {
      bifurcateData[svc] = {};
      for (const [regionKey, regionValue] of Object.entries(data['data'])) {
        bifurcateData[svc][regionKey] = regionValue[svc];
      }
    });
    setBifurcateSummary(bifurcateData);

    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedItem) {
      const dataRequest = {
        fileName: selectedItem?.['fileName'],
        userAccountId: selectedAccount,
      };

      if (selectedPlatform === PLATFORM.AWS) {
        GlobalScanApiService.getScanParsedResult(dataRequest)
          .then(data => {
            DebugService.log('GlobalScan: Scan Parsed Result', data);
            setGlobalScanParsedResultApiData(data?.data ? data : {});
            processParsedData(data);
            if (data['error']) {
              setSnackbarStatus('failed');
              setSnackbarMessage(data['error']?.message ?? data['error'] ?? 'Something went wrong');
              setIsLoading(false);
            }
          })
          .catch(() => {
            setSnackbarStatus('failed');
            setSnackbarMessage('Something went wrong while fetching result');
            setIsLoading(false);
          });
      } else {
        GlobalScanApiService.getAzureScanParsedResult(dataRequest)
          .then(data => {
            DebugService.log('GlobalScan: Azure Scan Parsed Result', data);
            setGlobalScanParsedResultApiData(data?.data ? data : {});
            processAzureParsedData(data);
            if (data['error']) {
              setSnackbarStatus('failed');
              setSnackbarMessage(data['error']?.message ?? data['error'] ?? 'Something went wrong');
              setIsLoading(false);
            }
          })
          .catch(() => {
            setSnackbarStatus('failed');
            setSnackbarMessage('Something went wrong while fetching result');
            setIsLoading(false);
          });
      }
    }
  }, [selectedItem]);

  const selectItemHandler = (e) => {
    setIsLoading(true);
    const idx = parseInt(e.target.value, 10);
    setSelectedItemIndex(idx);
    setSelectedItem(scanResult[idx]);
  };

  const fetchData = () => {
    if (selectedPlatform === PLATFORM.AWS) {
      GlobalScanApiService.getScanResult(selectedAccount)
        .then(data => {
          setGlobalScanResultApiData(data?.data ? data : {});
          if (!data) {
            setSnackbarStatus('failed');
            setSnackbarMessage('Data not received');
          } else {
            if (data['data']) {
              setScanResult(data['data'] ?? []);
              if (scanResultSelectRef.current && data['data'][0]) {
                setSelectedItem(data['data'][0]);
                setSelectedItemIndex(0);
              }
            }
            if (data['error']) {
              setSnackbarStatus('failed');
              setSnackbarMessage(data['error']?.message ?? data['error'] ?? 'Something went wrong');
            }
            if (data['isScanAvailable']) {
              setIsScanAvailable(data['isScanAvailable']);
            }
          }
          setIsLoading(false);
        })
        .catch(() => {
          setSnackbarStatus('failed');
          setSnackbarMessage('Something went wrong while fetching result');
          setIsLoading(false);
        });
    } else {
      GlobalScanApiService.getAzureScanResult(selectedAccount)
        .then(data => {
          setGlobalScanResultApiData(data?.data ? data : {});
          if (!data) {
            setSnackbarStatus('failed');
            setSnackbarMessage('Data not received');
          } else {
            if (data['data']) {
              setScanResult(data['data'] ?? []);
              if (scanResultSelectRef.current && data['data'][0]) {
                setSelectedItem(data['data'][0]);
                setSelectedItemIndex(0);
              }
            }
            if (data['error']) {
              setSnackbarStatus('failed');
              setSnackbarMessage(data['error']?.message ?? data['error'] ?? 'Something went wrong');
            }
            if (data['isScanAvailable']) {
              setIsScanAvailable(data['isScanAvailable']);
            }
          }
          setIsLoading(false);
        })
        .catch(() => {
          setSnackbarStatus('failed');
          setSnackbarMessage('Something went wrong while fetching result');
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Global Scan`;
    setSnackbarStatus(null);
    setSnackbarMessage(null);
    setIsLoading(false);
    setScanParsedResult({});
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
    setScanParsedResult({});
  }, [selectedAccount]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      globalScanParsedResultApiData,
      globalScanResultApiData,
    });
  }, [globalScanParsedResultApiData, globalScanResultApiData]);

  // Derive region summary from scanParsedResult
  useEffect(() => {
    if (Object.keys(scanParsedResult).length > 0) {
      const regions = Object.keys(scanParsedResult);
      setRegionList(regions);
      setSelectedRegion(regions[0]);
      setSelectedRegionDetails(Object.values(scanParsedResult)[0] ?? {});
    }
  }, [scanParsedResult]);

  // Derive chart data from scanParsedResult
  useEffect(() => {
    if (Object.keys(scanParsedResult).length > 0) {
      const firstRegionServices = Object.values(scanParsedResult)[0] ?? {};
      const keys = Object.keys(firstRegionServices);
      setChartKeys(keys);
      const newData = Object.entries(scanParsedResult).map(([region, services]) => {
        const obj = { name: region };
        keys.forEach(k => { obj[k] = services[k] ?? 0; });
        return obj;
      });
      setChartData(newData);
    }
  }, [scanParsedResult]);

  const handleRegionChange = (e) => {
    const region = e.target.value;
    setSelectedRegion(region);
    setSelectedRegionDetails(scanParsedResult[region] ?? {});
  };

  const hasParsedResult = Object.keys(scanParsedResult).length > 0;
  const summaryEntries = Object.entries({ ...parsedResultSummary, ...globalServices }).filter(([, v]) => v !== 0);

  return (
    <div style={{ padding: '20px 24px 48px', background: '#f4f6fa', minHeight: '100vh' }}>
      <style>{`
        .gs-cell-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
        .gs-cell-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .gs-cell { background: #fff; border: 1px solid #e7edf6; border-radius: 10px; padding: 8px 10px; box-shadow: 0 1px 2px rgba(16,24,40,.04); cursor: pointer; transition: box-shadow .15s; }
        .gs-cell:hover { box-shadow: 0 4px 14px rgba(16,24,40,.09); }
        .gs-cell .k { font-size: 11px; color: #6b7280; margin-bottom: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .gs-cell .v { font-size: 16px; font-weight: 750; letter-spacing: -.01em; color: #1d2433; }
        .gs-panels { display: grid; grid-template-columns: 1.5fr 1fr 1fr; gap: 16px; }
        @media (max-width:1200px) { .gs-panels { grid-template-columns: 1fr; } .gs-cell-grid-4 { grid-template-columns: repeat(2,1fr); } }
        @keyframes gs-spin { to { transform: rotate(360deg); } }
        .gs-spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: gs-spin .7s linear infinite; display: inline-block; }
      `}</style>

      {/* Scan Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Report select */}
        <div style={{ position: 'relative', background: '#fff', border: '1px solid #e6e9f0', borderRadius: 10, padding: '8px 36px 8px 14px', minWidth: 420, boxShadow: '0 1px 2px rgba(16,24,40,.04),0 4px 14px rgba(16,24,40,.05)' }}>
          <span style={{ position: 'absolute', top: -7, left: 12, background: '#fff', padding: '0 5px', fontSize: 10.5, color: '#6b7280' }}>Select</span>
          <select
            ref={scanResultSelectRef}
            value={selectedItemIndex >= 0 ? selectedItemIndex : ''}
            onChange={selectItemHandler}
            style={{ border: 'none', outline: 'none', fontSize: 13, fontWeight: 600, color: '#1f2937', background: 'transparent', width: '100%', fontFamily: 'ui-monospace,Menlo,monospace', cursor: 'pointer' }}
          >
            {scanResult.length === 0 && <option value="">No scan results available</option>}
            {scanResult.map((d, i) => (
              <option key={d['displayAs']} value={i}>{d['displayAs']}</option>
            ))}
          </select>
        </div>

        {/* Download button */}
        {hasParsedResult && (
          <button
            title="Click to Download"
            onClick={() => exportJSONToCSV(exportData, selectedItem['displayAs'])}
            style={{ background: '#3c485e', color: '#fff', border: 'none', borderRadius: 10, width: 40, height: 40, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <FiDownload />
          </button>
        )}

        {/* Scan button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <button
            onClick={accountScanHandler}
            disabled={!isScanAvailable || isLoading}
            style={{
              background: (!isScanAvailable || isLoading) ? '#9ca3af' : '#f5821f',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 22px',
              fontSize: 13.5,
              fontWeight: 700,
              cursor: (!isScanAvailable || isLoading) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(245,130,31,.28)',
            }}
          >
            {isLoading ? <span className="gs-spinner" /> : null}
            {isLoading ? 'Scanning...' : 'Click to Scan'}
            {!isLoading && <img src="/scan-icon.svg" alt="" width={23} height={23} />}
          </button>
          {!isScanAvailable && (
            <span style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>Scan will be available after sometime</span>
          )}
        </div>
      </div>

      {/* 3-panel grid */}
      <div className="gs-panels">

        {/* Panel 1 — Summary */}
        <div style={PANEL_STYLES}>
          <div style={PANEL_HEAD}>Summary</div>
          <div style={PANEL_BODY}>
            <div className="gs-cell-grid-4">
              {summaryEntries.map(([k, v]) => (
                <div
                  key={k}
                  className="gs-cell"
                  title="Click to view details"
                  onClick={() => setPopupService(k)}
                >
                  <div className="k">{toWholeStringByUnderscore(k)}</div>
                  <div className="v">{v ?? 0}</div>
                </div>
              ))}
              {!hasParsedResult && [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} style={{ height: 52, borderRadius: 10, background: 'linear-gradient(90deg,#f0f2f5 25%,#e8eaed 50%,#f0f2f5 75%)', backgroundSize: '200% 100%', animation: 'dt-shimmer 1.5s infinite' }} />
              ))}
            </div>
          </div>
        </div>

        {/* Panel 2 — Region Summary */}
        <div style={PANEL_STYLES}>
          <div style={PANEL_HEAD}>Region Summary</div>
          <div style={PANEL_BODY}>
            {/* Region picker */}
            {regionList.length > 0 && (
              <div style={{ position: 'relative', background: '#fff', border: '1px solid #e6e9f0', borderRadius: 10, padding: '7px 32px 7px 12px', marginBottom: 10 }}>
                <span style={{ position: 'absolute', top: -6, left: 10, background: '#fff', padding: '0 4px', fontSize: 10, color: '#6b7280' }}>Region</span>
                <select
                  value={selectedRegion}
                  onChange={handleRegionChange}
                  style={{ border: 'none', outline: 'none', fontSize: 13, fontWeight: 700, color: '#1f2937', background: 'transparent', width: '100%', cursor: 'pointer' }}
                >
                  {regionList.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Region cells */}
            <div className="gs-cell-grid-2">
              {Object.entries(selectedRegionDetails)
                .filter(([, v]) => v !== 0)
                .map(([k, v]) => (
                  <div key={k} className="gs-cell">
                    <div className="k">{toWholeStringByUnderscore(k)}</div>
                    <div className="v">{v ?? 0}</div>
                  </div>
                ))}
              {!hasParsedResult && [1, 2, 3, 4].map(i => (
                <div key={i} style={{ height: 52, borderRadius: 10, background: 'linear-gradient(90deg,#f0f2f5 25%,#e8eaed 50%,#f0f2f5 75%)', backgroundSize: '200% 100%', animation: 'dt-shimmer 1.5s infinite' }} />
              ))}
            </div>
          </div>
        </div>

        {/* Panel 3 — Regional Services Distribution */}
        <div style={PANEL_STYLES}>
          <div style={PANEL_HEAD}>Regional Services Distribution</div>
          <div style={{ ...PANEL_BODY, padding: '10px 8px' }}>
            {hasParsedResult ? (
              <div style={{ overflowX: 'auto' }}>
                <ResponsiveContainer width={Math.max(chartData.length * 100, 240)} height={320}>
                  <BarChart
                    data={chartData}
                    layout="horizontal"
                    margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    {chartKeys.map((key, i) => (
                      <Bar key={`bar${i}`} dataKey={key} stackId="a" fill={BarColorCode[i % BarColorCode.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', justifyContent: 'center', marginTop: 10, fontSize: 10.5, color: '#6b7280', padding: '0 6px' }}>
                  {chartKeys.slice(0, 10).map((key, i) => (
                    <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: BarColorCode[i % BarColorCode.length], display: 'inline-block', flexShrink: 0 }} />
                      {toWholeStringByUnderscore(key)}
                    </span>
                  ))}
                  {chartKeys.length > 10 && <span>+{chartKeys.length - 10} more</span>}
                </div>
              </div>
            ) : (
              <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
                Select a scan result to view distribution
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Service detail popup */}
      {popupService && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setPopupService(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', minWidth: 280, maxWidth: 420, boxShadow: '0 20px 50px rgba(0,0,0,.25)', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#1d2433' }}>{toWholeStringByUnderscore(popupService)}</span>
              <button onClick={() => setPopupService(null)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
            </div>
            {globalServices[popupService] ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #e6e9f0', fontSize: 14 }}>
                <span style={{ fontWeight: 600 }}>Global Service</span>
                <span>{globalServices[popupService]}</span>
              </div>
            ) : bifurcateSummary[popupService] ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <tbody>
                  {(Array.isArray(bifurcateSummary[popupService])
                    ? bifurcateSummary[popupService].map((v, i) => [i, v])
                    : Object.entries(bifurcateSummary[popupService])
                  ).filter(([, v]) => v !== 0).map(([k, v]) => (
                    <tr key={k} style={{ borderTop: '1px solid #e6e9f0' }}>
                      <td style={{ padding: '8px 0', fontWeight: 600, paddingRight: 16 }}>
                        {popupService !== 'regions' ? k : ''}
                      </td>
                      <td style={{ padding: '8px 0', color: '#1d4ed8' }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: '#6b7280', fontSize: 14 }}>No breakdown available.</p>
            )}
          </div>
        </div>
      )}

      {snackbarStatus && (
        <SnackbarNotification
          initialOpen={true}
          duration={5000}
          message={snackbarMessage}
          actionButtonName="Ok"
          theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
          yPosition="top"
          xPosition="center"
        />
      )}
    </div>
  );
}
